import { useCallback, useMemo } from 'react';
import type { FieldValues, UseFormProps, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from 'react-use';

import {
  Function_,
  CompanySize,
  Industry,
  InterestPersonal,
  InterestProfessional,
  OffLimitsReason,
  OrgType,
  PipelineAttendees,
  PipelineStatus,
  PipelineType,
  PipelineTypeFilter,
  ResponseType,
  Seniority,
} from 'client/types.gen';
import type {
  CandidateTier,
  CandidateType,
  ClientType,
  InterestRole,
  IntroductionType,
  PeerStatus,
  PipelineRoleType,
  PostTeamCandidatesFilterV3Data,
  RecommendationReviewState,
  TaskCreated,
  TaskDueDate,
  TaskStatus,
} from 'client/types.gen';
import { allowedCandidatePathways } from 'config/candidate';
import { useFormStepContext } from 'helpers/context';
import { INVOICE_STATUS_ORDER } from 'helpers/invoiceStatus';

type ConnectionStatus = NonNullable<
  NonNullable<
    PostTeamCandidatesFilterV3Data['body']['filter_data']
  >['connection_status']
>[number];
type OperatingExperience = NonNullable<
  NonNullable<
    PostTeamCandidatesFilterV3Data['body']['filter_data']
  >['operating_highlights']
>[number];

export const getFormDataPlaceholder = () => '--';

// TODO: Remove this helper once react-hook-form has a cleaner API for async default values
// learn more: https://github.com/react-hook-form/react-hook-form/issues/2492
export const useFormAsyncDefaultValues = <
  TFieldValues extends FieldValues = FieldValues,
  TContext extends object = object,
>(
  useFormOptions: UseFormProps<TFieldValues, TContext>,
): UseFormReturn<TFieldValues, TContext> => {
  const { defaultValues } = useFormOptions;

  const useFormMethods = useForm<TFieldValues, TContext>(useFormOptions);

  const { reset } = useFormMethods;

  useDeepCompareEffect(() => {
    // @ts-expect-error
    reset(defaultValues);
  }, [defaultValues, reset]);

  return useFormMethods;
};

export const useOptionsPipelineRoleTypeMap = () => {
  const { t } = useTranslation();

  const optionsPipelineRoleTypeMap: Record<
    keyof typeof PipelineRoleType,
    string
  > = useMemo(
    () => ({
      BOARD: t('form.pipeline.field.role_type.option.board'),
      EXECUTIVE: t('form.pipeline.field.role_type.option.executive'),
      GENERAL: t('form.pipeline.field.role_type.option.operating'),
      OPERATING: t('form.pipeline.field.role_type.option.operating'),
      OTHER: t('form.pipeline.field.role_type.option.other'),
    }),
    [t],
  );

  return optionsPipelineRoleTypeMap;
};

export const useOptionsFunction = () => {
  const { t } = useTranslation();

  return Object.values(Function_).map((value) => ({
    label: t(`form.default.function.option.${value}`),
    value,
  }));
};

export const useOptionsSeniority = () => {
  const { t } = useTranslation();

  return Object.values(Seniority).map((value) => ({
    label: t(`form.default.seniority.option.${value}`),
    value,
  }));
};

export const useOptionsIndustry = () => {
  const { t } = useTranslation();

  return Object.values(Industry).map((value) => ({
    label: t(`form.default.industry.option.${value}`),
    value,
  }));
};

export const useOptionsInterestsPersonal = () => {
  const { t } = useTranslation();

  return Object.values(InterestPersonal).map((value) => ({
    label: t(`form.default.interests_personal.option.${value}`),
    value,
  }));
};

export const useOptionsInterestsProfessional = () => {
  const { t } = useTranslation();

  return Object.values(InterestProfessional).map((value) => ({
    label: t(`form.default.interests_professional.option.${value}`),
    value,
  }));
};

export const useOptionsPipelineStatus = () => {
  const { t } = useTranslation();

  return Object.values(PipelineStatus).map((value) => ({
    label: t(`form.default.pipeline_status.option.${value}`),
    value,
  }));
};

/** TODO: transition this to the lowercase enum value version of PipelineType. */
export const useOptionsPipelineType = () => {
  const { t } = useTranslation();

  return Object.values(PipelineType).map((value) => ({
    label: t(
      `form.default.pipeline_type.option.${value.toLowerCase() as Lowercase<typeof value>}.label`,
    ) as string,
    value,
  }));
};

// TODO: deprecate this once the lowercase enum version of PipelineType is standard (above)
export const useOptionsPipelineTypeFilter = () => {
  const { t } = useTranslation();

  return Object.values(PipelineTypeFilter).map((value) => ({
    label: t(`form.default.pipeline_type.option.${value}.label`),
    value,
  }));
};

// Special case: this applies to candidates and users from Acrew only.
export const useOptionsSourceCompanyTags = () => {
  const { t } = useTranslation();

  return [
    {
      label: t('form.default.source_company_tags.option.acrew_tier_1'),
      value: 'acrew_tier_1',
    },
    {
      label: t('form.default.source_company_tags.option.acrew_tier_2'),
      value: 'acrew_tier_2',
    },
    {
      label: t('form.default.source_company_tags.option.acrew_tier_3'),
      value: 'acrew_tier_3',
    },
  ];
};

export const useOptionsAttendees = () => {
  const { t } = useTranslation();

  return [
    {
      label: t(`form.default.attendees.option.1-10`),
      value: PipelineAttendees['1_10'],
    },
    {
      label: t(`form.default.attendees.option.11-20`),
      value: PipelineAttendees['11_20'],
    },
    {
      label: t(`form.default.attendees.option.21-50`),
      value: PipelineAttendees['21_50'],
    },
  ];
};

export const useOptionsTeamType = () => {
  const { t } = useTranslation();

  return Object.values(OrgType).map((value) => ({
    label: t(`form.default.team_type.option.${value}`),
    value,
  }));
};

export const useOptionsTier = () => {
  const { t } = useTranslation();

  // not using enum because we want to override the items order
  const values: CandidateTier[] = [
    'executive_client',
    'former_executive_client',
    'executive_client_1st_degree_referral',
    'executive_client_2nd_degree_referral',
    'advisor_network',
    'enterprise_partner_1st_degree_referral',
    'research',
    'b4b',
    'external',
  ];

  return values.map((value) => ({
    label: t(`form.candidate.field.tier.option.${value}`),
    value,
  }));
};

export const useOptionsInvoiceStatus = () => {
  const { t } = useTranslation();

  return INVOICE_STATUS_ORDER.map((value) => ({
    label: t(`form.default.invoice_status.option.${value}`),
    value,
  }));
};

export const useOptionsOffLimitsReason = () => {
  const { t } = useTranslation();

  return Object.values(OffLimitsReason).map((value) => ({
    label: t(`form.candidate.field.reason.option.${value}`),
    value,
  }));
};

export const useOptionsClientType = () => {
  const { t } = useTranslation();

  const values: ClientType[] = [
    'private_advisor',
    'positioner_services',
    'brand_strategic_identity',
    'portage_main',
  ];

  return values.map((value) => ({
    label: t(`form.candidate.field.type.option.${value}`),
    value,
  }));
};

export const useOptionsGoalProgram = () => {
  const { t } = useTranslation();

  const values: CandidateType[] = [
    'job_seeker_active',
    'job_seeker_passive',
    'board_member_aspiring',
    'network_builder',
    'new_role_leader',
    'uploaded_network',
  ];

  return values.map((value) => ({
    label: t(`form.goalProgram.option.${value}`),
    value,
  }));
};

export const useOptionsPathway = () => {
  const { t } = useTranslation();

  return allowedCandidatePathways.map((value) => ({
    label: t(`form.candidate.field.pathway.option.${value}`),
    value,
  }));
};

export const useOptionsCampaignSpread = () => {
  const { t } = useTranslation();

  return [
    {
      label: t('form.default.campaign_spread.option.first_available'),
      value: 'first_available',
    },
    {
      label: t('form.default.campaign_spread.option.custom'),
      value: 'custom',
    },
  ];
};

export const useOptionsCampaignWeeks = ({
  startDate,
}: {
  startDate?: Date | null;
}) => {
  const { t } = useTranslation();
  const optionsCampaignWeeks = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        const weekOffset = i + 1;
        const formattedDate = startDate
          ? `(${new Date(
              startDate.getTime() + weekOffset * 7 * 24 * 60 * 60 * 1000,
            ).toLocaleDateString('en', {
              day: 'numeric',
              month: 'short',
              weekday: 'long',
              year: 'numeric',
            })})`
          : undefined;
        return {
          label: t('form.trending.field.campaign_weeks.option', {
            count: weekOffset,
            date: formattedDate,
          }),
          value: weekOffset,
        };
      }),
    [startDate, t],
  );

  return optionsCampaignWeeks;
};

export const useOptionsRoleInterest = () => {
  const { t } = useTranslation();

  // not using enum because we want to override the items order
  const values: InterestRole[] = [
    'seeking_actively',
    'seeking_passively',
    'seeking_no',
  ];

  return values.map((value) => ({
    label: t(`form.default.interestRole.option.${value}`),
    value,
  }));
};

export const useOptionsOperatingHighlights = () => {
  const { t } = useTranslation();

  // not using enum because we want to override the items order
  const values: OperatingExperience[] = [
    'early_stage',
    'part_of_ipo',
    'led_ipo',
    'private_equity_backed',
    'venture_capital_backed',
    'exit',
  ];

  return values.map((value) => ({
    label: t(`form.default.operatingHighlights.option.${value}`),
    value,
  }));
};

export const useOptionsConnectionStatus = () => {
  const { t } = useTranslation();

  // not using enum because we want to exclude some values
  const values: ConnectionStatus[] = ['is_favorite', 'introduced'];

  return values.map((value) => ({
    label: t(`form.default.connectionStatus.option.${value}`),
    value,
  }));
};

export const useOptionsCompanyType = () => {
  const { t } = useTranslation();

  // not using enum because we want to exclude some values
  const values: OrgType[] = ['investor', 'search', 'advisory', 'corporate'];

  return values.map((value) => ({
    label: t(`status.customer.${value}`),
    value,
  }));
};

export const useOptionsCompanySize = () => {
  const { t } = useTranslation();

  return Object.values(CompanySize).map((value) => ({
    label: t(`form.pipeline.field.company_size.option.${value}`),
    value,
  }));
};

export const useOptionsPeerStatus = () => {
  const { t } = useTranslation();

  // not using enum because we want to override the items order
  const values: PeerStatus[] = [
    'published',
    'info_needed',
    'approved',
    'declined_advisor',
    'declined_candidate',
    'scheduled',
    'intro_made',
  ];

  return values.map((value) => ({
    label: t(`page.peers.status.${value}`),
    value,
  }));
};

export const useOptionsRecommendationReviewState = () => {
  const { t } = useTranslation();

  // not using enum because we want to exclude some values
  const values: RecommendationReviewState[] = [
    'intro_requested',
    'intro_made',
    'not_interesting',
    'needs_review',
  ];

  return values.map((value) => ({
    label: t(`page.candidate.response.${value}`),
    value,
  }));
};

export const useOptionsRoleType = () => {
  const label = useOptionsPipelineRoleTypeMap();

  const optionsRoleType: Array<{
    label: string;
    value: keyof typeof PipelineRoleType;
  }> = [
    {
      label: label.OPERATING,
      value: 'OPERATING',
    },
    {
      label: label.BOARD,
      value: 'BOARD',
    },
    {
      label: label.EXECUTIVE,
      value: 'EXECUTIVE',
    },
    {
      label: label.OTHER,
      value: 'OTHER',
    },
  ];

  return optionsRoleType;
};

export const useOptionsResponseType = () => {
  const { t } = useTranslation();

  return Object.values(ResponseType).map((value) => ({
    label: t(`form.default.response_type.${value}`),
    value,
  }));
};

export const useOptionsRecommendationType = () => {
  const { t } = useTranslation();

  const optionsRecommendationType: Array<{
    label: string;
    value: IntroductionType;
  }> = [
    {
      label: t('page.candidate.recommendation.pipeline'),
      value: 'pipeline',
    },
    {
      label: t('page.candidate.recommendation.trending'),
      value: 'team',
    },
    // TODO: add 'search' (global intro) recommendation type
    // {
    //   label: t('page.candidate.recommendation.global'),
    //   value: 'search',
    // },
  ];

  return optionsRecommendationType;
};

export const useOptionsTaskCreated = () => {
  const { t } = useTranslation();

  // not using enum because we want to override the items order
  const values: TaskCreated[] = [
    'today',
    'yesterday',
    'this_week',
    'one_week_ago',
    'two_weeks_ago',
    'one_month_ago',
    'older_than_one_month',
  ];

  return values.map((value) => ({
    label: t(`page.tasks.created.${value}`),
    value,
  }));
};

export const useOptionsTaskDueDate = () => {
  const { t } = useTranslation();

  // not using enum because we want to override the items order
  const values: TaskDueDate[] = ['this_week', 'next_week'];

  return values.map((value) => ({
    label: t(`page.tasks.dueDate.${value}`),
    value,
  }));
};

export const useOptionsTaskStatus = () => {
  const { t } = useTranslation();

  // not using enum because we want to override the items order
  const values: TaskStatus[] = ['incomplete', 'completed'];

  return values.map((value) => ({
    label: t(`page.tasks.status.${value}`),
    value,
  }));
};

export const useWizardFormNavigation = () => {
  const [, setStep] = useFormStepContext();

  const goToStep = useCallback(
    (step: number) => {
      setStep(([currentStep, maxSteps]) => [
        step > 0 && step <= maxSteps ? step : currentStep,
        maxSteps,
      ]);
    },
    [setStep],
  );

  const onNextStep = useCallback(() => {
    setStep(([currentStep, maxSteps]) => [
      currentStep < maxSteps ? currentStep + 1 : currentStep,
      maxSteps,
    ]);
  }, [setStep]);

  const onPreviousStep = useCallback(() => {
    setStep(([currentStep, maxSteps]) => [
      currentStep > 1 ? currentStep - 1 : currentStep,
      maxSteps,
    ]);
  }, [setStep]);

  return useMemo(
    () => ({
      goToStep,
      onNextStep,
      onPreviousStep,
    }),
    [goToStep, onNextStep, onPreviousStep],
  );
};
